import { useEffect, useState } from "react";
import axios from "axios";
import { MessageSquare, Loader2, Send, HelpCircle, Sparkles } from "lucide-react";
import { getStoredEmail } from "../lib/useNewsezeUser";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * AskUsSection — replaces the old comments box on every article.
 *
 * Reader types a question about the story. Newseze's algorithm answers
 * grounded in the article's headline + summary, with citations and a
 * legal/fair-use footer.
 *
 * Both question and answer become a public Q&A entry — the next reader
 * sees a growing knowledge base on each story.
 */
export default function AskUsSection({ articleId }) {
  const email = getStoredEmail();
  const [qa, setQa] = useState([]);
  const [busy, setBusy] = useState(true);
  const [question, setQuestion] = useState("");
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");
  const [quota, setQuota] = useState(null);

  const load = async () => {
    setBusy(true);
    try {
      const r = await axios.get(`${API}/articles/${articleId}/ask-us`, { timeout: 8000 });
      setQa(r.data.qa || []);
    } catch {}
    finally { setBusy(false); }
  };

  useEffect(() => { load(); }, [articleId]);

  const submit = async (e) => {
    e.preventDefault();
    if (question.trim().length < 4) { setErr("Please ask a question with at least 4 characters."); return; }
    setSubmitting(true); setErr("");
    try {
      const body = {
        article_id: articleId,
        question: question.trim(),
        display_name: name.trim() || undefined,
      };
      if (email) body.email = email;
      const r = await axios.post(`${API}/articles/${articleId}/ask-us`, body, { timeout: 60000 });
      setQa([r.data.qa, ...qa]);
      setQuestion("");
      setQuota(r.data.quota);
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (typeof d === "object" && d !== null) {
        setErr(d.message || "Daily Ask Us limit reached.");
      } else {
        setErr(d || "Could not get an answer right now. Try again.");
      }
    } finally { setSubmitting(false); }
  };

  return (
    <section className="mt-8 rounded-2xl border border-[var(--line)] bg-white p-6" data-testid="ask-us-section">
      <div className="flex items-center gap-2 mb-2">
        <span className="text-[10px] uppercase tracking-[0.18em] font-black text-[var(--gold)] bg-[#1d1303] rounded-full px-2 py-0.5 flex items-center gap-1">
          <Sparkles size={11} /> Ask Us · Any Story, Any Answer
        </span>
        <span className="text-[10px] uppercase tracking-[0.14em] font-bold text-[var(--muted)]" data-testid="ask-us-count">
          {qa.length === 0 ? "Be the first to ask" : `${qa.length} question${qa.length === 1 ? "" : "s"} answered`}
        </span>
      </div>
      <p className="text-[12.5px] text-[var(--muted)] mb-4 leading-snug">
        Newseze's algorithm reads the story and answers your question — calmly, factually, with source attribution.
        No comments, no flame wars — just answers.
      </p>

      <form onSubmit={submit} className="mb-5" data-testid="ask-us-form">
        <div className="grid grid-cols-1 sm:grid-cols-[200px_1fr] gap-2 mb-2">
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Display name (optional)"
            className="border border-[var(--line)] rounded-md p-2 text-[13px]"
            data-testid="ask-us-name"
            maxLength={60}
          />
        </div>
        <div className="relative">
          <HelpCircle size={14} className="absolute left-3 top-3 text-[var(--muted)] pointer-events-none" />
          <textarea
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            placeholder="Ask anything about this story — &quot;Why does this matter for my 401(k)?&quot; &quot;Who benefits if this passes?&quot;"
            className="w-full border border-[var(--line)] rounded-md p-2 pl-9 text-[13.5px]"
            rows={2}
            maxLength={400}
            data-testid="ask-us-input"
          />
        </div>
        <div className="flex items-center justify-between mt-2 gap-2 flex-wrap">
          {err && <span className="text-[12px] text-rose-700 italic" data-testid="ask-us-error">{err}</span>}
          {quota && (
            <span className="text-[11px] text-[var(--muted)] font-bold" data-testid="ask-us-quota">
              {quota.used}/{quota.cap} today · {quota.tier.toUpperCase()}
            </span>
          )}
          <button
            type="submit"
            disabled={submitting || question.trim().length < 4}
            className="btn primary ml-auto disabled:opacity-50"
            data-testid="ask-us-submit"
          >
            {submitting ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
            Ask Newseze
          </button>
        </div>
      </form>

      {busy ? (
        <div className="text-[var(--muted)] text-[13px] flex items-center gap-2">
          <Loader2 size={13} className="animate-spin" /> Loading questions…
        </div>
      ) : qa.length === 0 ? (
        <p className="text-[13px] text-[var(--muted)] italic">No questions yet. Be the first.</p>
      ) : (
        <ul className="space-y-4">
          {qa.map((entry) => (
            <li key={entry.id} className="border-b border-[var(--line)] pb-4 last:border-0" data-testid={`ask-us-entry-${entry.id}`}>
              <div className="flex items-start gap-2 text-[12.5px] mb-2">
                <span className="font-black text-[var(--ink)]">{entry.display_name || "Reader"}</span>
                <span className="text-[var(--muted)]">·</span>
                <span className="text-[var(--muted)]">
                  {new Date(entry.created_at).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                </span>
              </div>
              <div className="flex items-start gap-2 mb-2">
                <HelpCircle size={14} className="text-[var(--blue)] mt-0.5 shrink-0" />
                <p className="text-[14px] font-black text-[var(--ink)] leading-snug">{entry.question}</p>
              </div>
              <div className="flex items-start gap-2 ml-1 pl-4 border-l-2 border-[var(--gold)]">
                <Sparkles size={13} className="text-[var(--gold)] mt-0.5 shrink-0" />
                <p className="text-[13.5px] text-[var(--ink)] leading-snug whitespace-pre-wrap">{entry.answer}</p>
              </div>
            </li>
          ))}
        </ul>
      )}

      <p className="text-[10.5px] text-[var(--muted)] italic mt-5 border-t border-[var(--line)] pt-3">
        Answers reflect Newseze's editorial framework applied under fair use (17 U.S.C. § 107).
        Not financial, legal, medical, or tax advice. Hate speech and racial slurs are blocked.
      </p>
    </section>
  );
}
